import mime from 'mime';

import filesize from 'filesize';
import type { IFile } from '$provider/lib/apis/interfaces/IResponse';
import DateString from '$shared/models/DateString';
import { getFormatedDateString } from '$shared/helpers/date';
import { DateFormat } from '$provider/constants/Date';
import { caqhDocumentData } from '$provider/lib/datas/CaqhDocumentData';
import { PreviewSupportedFileType } from '$shared/constants/PreviewSupportedFileType';
import SupportedAttachFileType from '$shared/constants/SupportedAttachFileType';

export default class File {
  readonly id: string;
  readonly providerId: string;
  readonly createdAt: DateString;
  readonly createdByUserId: string;
  readonly createdByUserEmail: string;
  readonly path: string;
  readonly size: number;

  private computedName: string;

  constructor(file: IFile) {
    this.id = file.id;
    this.providerId = file.providerId;
    this.createdByUserEmail = file.createdByUserEmail;
    this.createdByUserId = file.createdByUserId;
    this.path = file.path;
    this.size = file.size;
    this.createdAt = new DateString(file.createdAt);
    this.computedName = File.getComputedFileNameByPath(this.path) || '';
  }

  get name(): string {
    return this.computedName || this.path.split('/').pop() || '';
  }

  public get fileType(): string {
    return this.path?.split('.')?.pop() || '';
  }

  public get mimeType(): string | null {
    return mime.getType(this.fileType);
  }

  get folderPath(): string {
    return this.path.replace(`/uploads/providers/${this.providerId}/`, '').replace(this.name, '');
  }

  get isSupportedFileType(): boolean {
    return File.checkMimeTypeSupportsPreview(this.mimeType);
  }

  get isSupportAttachment(): boolean {
    return Object.values(SupportedAttachFileType).some((type) => this.mimeType?.includes(type));
  }

  get createdDate(): string {
    return getFormatedDateString(this.createdAt?.getRawString(), DateFormat.Default) || '';
  }

  get sizeLabel(): string {
    return filesize(this.size) || '0';
  }

  get isPending(): boolean {
    return !!caqhDocumentData.getInfo(this.id)?.isPending;
  }

  public getAdditionalInfo(): string {
    return caqhDocumentData.getInfo(this.id)?.toWellFormed() || '';
  }

  static checkMimeTypeSupportsPreview(mimeType: string | null): boolean {
    return Object.values(PreviewSupportedFileType).some((type) => mimeType?.includes(type));
  }

  static getComputedFileNameByPath(path: string): string | undefined {
    const prefixRegex = new RegExp('^/uploads/providers/[a-z,0-9,-]{36,36}', 'g');
    if (!path.match(prefixRegex)) return;

    const prunePath = path.replace(prefixRegex, '');
    const splittedPath = prunePath.split('/').filter(Boolean);
    // 2 or below it means folder and file are in 1st level, if it goes more that 2, that means multiple `/` found
    if (splittedPath.length <= 2) return;

    splittedPath.splice(0, 1);
    const fileName = splittedPath.join('/');
    return fileName;
  }
}
